import { mdiClose } from '@mdi/js'
import Icon from '@mdi/react'
import Drawer from '@mui/material/Drawer'
import IconButton from '@mui/material/IconButton'
import * as React from 'react'
import { useIntl } from 'react-intl'
import { OVERLAY_SIZES, OverlaySize } from 'components/overlaySizes'
import { update as updateIntercom } from 'services/intercom'
import styles from './PPDrawer.pcss'

const DEFAULT_SIZE: OverlaySize = 'md'

// MUI puts temporary drawers on its `drawer` tier (1200), which is meant for docked/persistent
// drawers. PPDrawer is always a modal with a backdrop, so it belongs on the `modal` tier (1300) —
// the level the rest of the app already assumes (e.g. TrialWidget sits at 1299 to stay under it).
// Do not raise this further: the app's popper/dropdown layer lives at 1301-1303 and has to stay
// above drawers it renders inside of.
const DEFAULT_Z_INDEX = 1300

// Intercom's launcher sits at z-index 2147483003 — near the int32 ceiling, and its own private
// value — so it covers the drawer footer actions. Rather than outbid it, hide the launcher while
// a drawer is open. Module-level count because several drawers can be open at once (one opening
// another); only the last one to close restores the launcher.
let openDrawerCount = 0

function useHideIntercomLauncher(open: boolean) {
  React.useEffect(() => {
    if (!open) {
      return
    }
    openDrawerCount += 1
    if (openDrawerCount === 1) {
      updateIntercom({ hide_default_launcher: true })
    }
    return () => {
      openDrawerCount -= 1
      if (openDrawerCount === 0) {
        updateIntercom({ hide_default_launcher: false })
      }
    }
  }, [open])
}

export interface PPDrawerProps {
  open: boolean
  title: React.ReactNode
  onClose: () => void
  footer?: React.ReactNode
  size?: OverlaySize
  // Override the drawer's stacking order. Defaults to the modal tier (1300); raise only when the
  // drawer must sit above another overlay that is itself above that tier.
  zIndex?: number
  // Let focus leave the drawer while it is open. Needed when the content opens a portalled overlay
  // that runs its own focus trap (a picker popper, say) - the two traps otherwise fight over focus.
  // Costs the modal's focus containment, so set it only for that case.
  disableEnforceFocus?: boolean
  className?: string
  children: React.ReactNode
  'data-testid'?: string
}

export function PPDrawer(props: PPDrawerProps) {
  const intl = useIntl()
  const titleId = React.useId()
  const width = OVERLAY_SIZES[props.size ?? DEFAULT_SIZE]
  useHideIntercomLauncher(props.open)
  return (
    <Drawer
      anchor="right"
      open={props.open}
      onClose={props.onClose}
      data-testid={props['data-testid']}
      classes={{ paper: `${styles.paper} ${props.className || ''}` }}
      PaperProps={{ style: { width } }}
      ModalProps={{ style: { zIndex: props.zIndex ?? DEFAULT_Z_INDEX }, disableEnforceFocus: props.disableEnforceFocus }}
    >
      <div className={styles.root} role="dialog" aria-modal="true" aria-labelledby={titleId}>
        <header className={styles.header}>
          <h2 id={titleId} className={styles.title}>{props.title}</h2>
          <IconButton
            className={styles['btn-close']}
            aria-label={intl.formatMessage({ id: 'actions.close' })}
            onClick={props.onClose}
          >
            <Icon path={mdiClose} size="20px" />
          </IconButton>
        </header>
        <div className={styles.body}>{props.children}</div>
        {props.footer ? <footer className={styles.footer}>{props.footer}</footer> : null}
      </div>
    </Drawer>
  )
}

export default PPDrawer
